/* @flow */
import * as React from 'react';
import { computed, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import type { TranslatorProps } from 'react-i18next';
import { translate } from 'react-i18next';

import Loader from '../../components/Loading/Loading';
import AutoCompleteModel from 'models/forms/AutoCompleteModel.js';

import ElectroTextField from '../components/inputs/ElectroTextField/ElectroTextField';
import ElectroSelectExtra from '../components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import ElectroDatePicker from '../components/inputs/ElectroDatePicker/ElectroDatePicker';
import ElectroResponseMessage from '../components/inputs/ElectroResponseMessage';
import ActionDrawerWrapper from 'components/ActionDrawerWrapper/ActionDrawerWrapper';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import FormWrapper from '../../components/FormWrapper/FormWrapper';
import FormSection from '../../components/FormSection/FormSection';
import FormControls from '../components/controls/FormControls/FormControls';

import {
  AddNewCostCenter,
  AddNewCompany,
  AddNewSite,
  AddNewBuilding,
  AddNewRoom,
  AddNewSystem,
} from 'views/ActionDrawer';

import {
  autoCompleteTradingAccounts,
  autoCompleteAccountCompanies,
  autoCompleteCompanySites,
  autoCompleteBuildings,
  autoCompleteAccountUsers,
  autoCompleteAccountCostCenter,
} from 'api/forms.js';

import { createNewLocation } from 'permissions/account';

import { createNewCostCenter } from 'permissions/costCenter';

import { makeFields, projectCreate, projectEdit } from './setupProjectForm';

const actionsListData: any = [
  {
    label: 'general:actions.new_cost_center',
    icon: '#icon-add',
    action: 'newCostCenter',
    permissions: createNewCostCenter,
  },
  {
    label: 'general:actions.new_company',
    icon: '#icon-add',
    action: 'newCompany',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_site',
    icon: '#icon-add',
    action: 'newSite',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_building_transport',
    icon: '#icon-add',
    action: 'newBuilding',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_lab_room',
    icon: '#icon-add',
    action: 'newRoom',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_system_workspace',
    icon: '#icon-add',
    action: 'newSystem',
    permissions: createNewLocation,
  },
];

type TypeProps = {|
  mode: string,
  projectData: Object,
|} & TranslatorProps;

@translate()
@inject('projectDetailsStore', 'actionDrawerStore')
@observer
export default class ProjectForm extends React.Component<*> {
  /*eslint-disable max-len*/

  @observable accountsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteTradingAccounts);

  @observable companyModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAccountCompanies({ assetRequestLocation: 'all' })
  );

  @observable sitesModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteCompanySites);

  @observable buildingsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);

  @observable roomsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);

  @observable systemsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);

  @observable usersModelLead: AutoCompleteModel = new AutoCompleteModel(autoCompleteAccountUsers);

  @observable usersModelEngineers: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAccountUsers
  );

  @observable projectCostCenter: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAccountCostCenter
  );

  @observable localState = {
    selectedValueCC: '',
    selectedValueEn: '',
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
  };

  props: TypeProps;

  toggleCostField = () => {
    this.isCostFieldToggled = !this.isCostFieldToggled;
  };

  getMode = () => this.props.mode;

  componentDidMount() {
    const projectData: Object =
      typeof this.props.projectData === 'undefined' ? {} : this.props.projectData;

    let costCentersStr: string = '';
    let engineersStr: string = '';

    if (projectData && projectData.cost_centres && projectData.cost_centres.length > 0) {
      for (let i: * = 0; i < projectData.cost_centres.length; i++) {
        costCentersStr !== '' && (costCentersStr += ',');
        costCentersStr += projectData.cost_centres[i].id;
      }
    }

    if (projectData && projectData.engineers && projectData.engineers.length > 0) {
      for (let i: * = 0; i < projectData.engineers.length; i++) {
        engineersStr !== '' && (engineersStr += ',');
        engineersStr += projectData.engineers[i].id;
      }
    }

    this.localState.selectedValueCC = costCentersStr;
    this.localState.selectedValueEn = engineersStr;
  }

  @computed get form(): Object {
    const projectData: Object =
      typeof this.props.projectData === 'undefined' ? {} : this.props.projectData;
    const fields: Array<*> = makeFields(projectData, this.props.t);
    // const fields: Array<*> = makeFields(this.props.projectDetailsStore.details);

    return this.getMode() === 'edit'
      ? projectEdit(fields, projectData, this.localState, this.props.t)
      : projectCreate(fields, this.localState, this.props.t);
  }

  handleOnChangeCCenter = (selectedValue: string) => {
    this.localState.selectedValueCC = selectedValue;
  };

  handleOnChangeEngineer = (selectedValue: string) => {
    this.localState.selectedValueEn = selectedValue;
  };

  resetLocations() {
    const resetModels = [
      this.companyModel,
      this.sitesModel,
      this.buildingsModel,
      this.roomsModel,
      this.systemsModel,
    ];
    const resetFields = [
      this.form.$('location.locationCompany'),
      this.form.$('location.locationSite'),
      this.form.$('location.locationBuilding'),
      this.form.$('location.locationRoom'),
      this.form.$('location.locationSystem'),
      // Extra Fields
      this.form.$('assignCostCentre.costCentreAssign'),
      this.form.$('assignProjectEngineers.projectEngineer'),
    ];

    // Reset Models
    for (let i: * = 0; i < resetModels.length; i++) {
      resetModels[i].clear();
      resetModels[i].loadData();
    }

    // Reset Fields
    for (let i: * = 0; i < resetFields.length; i++) {
      resetFields[i].value = '';
      resetFields[i].extra.selectID = '';
    }

    // Reset values on cost center and engineers
    this.localState.selectedValueCC = '';
    this.localState.selectedValueEn = '';
  }

  render(): React.Element<*> {
    const t: Function = this.props.t;
    const projectDetailsStore: Object = this.props.projectDetailsStore;
    const form: Object = this.form;

    const accountsModel: Object = this.accountsModel;
    const companyModel: Object = this.companyModel;
    const sitesModel: Object = this.sitesModel;
    const buildingsModel: Object = this.buildingsModel;
    const roomsModel: Object = this.roomsModel;
    const systemsModel: Object = this.systemsModel;
    const usersModelLead: Object = this.usersModelLead;
    const usersModelEngineers: Object = this.usersModelEngineers;
    const projectCostCenter: Object = this.projectCostCenter;

    return projectDetailsStore.loadingProjectDetails || this.localState.formIsLoading ? (
      <Loader />
    ) : (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <ElectroResponseMessage localState={this.localState} />
          <FormWrapper form={form}>
            {/* ================| CHOOSE ACCOUNT |================ */}
            <FormSection form={form} section="chooseAccount">
              <ElectroSelectExtra
                field={form.$('chooseAccount.tradingAccount')}
                form={form}
                formName="chooseAccount"
                labelKey="name"
                // updateCustomFields={() => this.updateCustomFields()}
                resetModels={() => this.resetLocations()}
                props={{
                  model: accountsModel,
                  modelChild: [
                    companyModel,
                    usersModelLead,
                    usersModelEngineers,
                    projectCostCenter,
                  ],
                }}
              />
            </FormSection>
            {/* ================| PROJECT DETAIL |================ */}
            <FormSection form={form} section="projectDetails">
              <ElectroTextField field={form.$('projectDetails.projectCode')} />
              <ElectroTextField field={form.$('projectDetails.projectName')} />
              <ElectroTextField
                field={form.$('projectDetails.projectDescription')}
                props={{
                  multiline: true,
                  rows: 5,
                }}
              />
              <ElectroDatePicker field={form.$('projectDetails.projectStartDate')} />
              <ElectroDatePicker field={form.$('projectDetails.projectEndDate')} />
              <ElectroSelectExtra
                field={form.$('projectDetails.chooseLead')}
                form={form}
                formName="projectDetails"
                labelKey={['first_name', 'last_name']}
                props={{
                  model: usersModelLead,
                }}
              />
              {/* <ElectroSelect field={form.$('projectDetails.chooseStatus')} searchable={false} simpleValue={true} /> */}
            </FormSection>
            {/* ================| ASSIGN COST CENTER |================ */}
            <FormSection form={form} section="assignCostCentre">
              <ElectroSelectExtra
                field={form.$('assignCostCentre.costCentreAssign')}
                form={form}
                formName="assignCostCentre"
                labelKey={['code', 'name']}
                actionButtonData={{
                  data: actionsListData[0],
                  disabled: !form.$('chooseAccount.tradingAccount').extra.selectID,
                }}
                multi={true}
                handleOnChange={this.handleOnChangeCCenter}
                singleSelectedValue={this.localState.selectedValueCC}
                props={{
                  model: projectCostCenter,
                }}
              />
            </FormSection>
            {/* ================| ASSIGN PROJECT ENGINEERS |================ */}
            <FormSection form={form} section="assignProjectEngineers">
              <ElectroSelectExtra
                field={form.$('assignProjectEngineers.projectEngineer')}
                form={form}
                formName="assignProjectEngineers"
                labelKey={['first_name', 'last_name']}
                multi={true}
                handleOnChange={this.handleOnChangeEngineer}
                singleSelectedValue={this.localState.selectedValueEn}
                props={{
                  model: usersModelEngineers,
                }}
              />
            </FormSection>
            {/* ================| PROJECT LOCATION |================ */}
            <FormSection form={form} section="location">
              <ElectroSelectExtra
                field={form.$('location.locationCompany')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  data: actionsListData[1],
                  disabled: !form.$('chooseAccount.tradingAccount').extra.selectID,
                }}
                props={{
                  model: companyModel,
                  modelChild: sitesModel,
                }}
              />
              <ElectroSelectExtra
                field={form.$('location.locationSite')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  data: actionsListData[2],
                  disabled: !form.$('location.locationCompany').extra.selectID,
                }}
                props={{
                  model: sitesModel,
                  modelChild: buildingsModel,
                }}
              />
              <ElectroSelectExtra
                field={form.$('location.locationBuilding')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  data: actionsListData[3],
                  disabled: !form.$('location.locationSite').extra.selectID,
                }}
                props={{
                  model: buildingsModel,
                  modelChild: roomsModel,
                }}
              />
              <ElectroSelectExtra
                field={form.$('location.locationRoom')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  data: actionsListData[4],
                  disabled: !form.$('location.locationBuilding').extra.selectID,
                }}
                props={{
                  model: roomsModel,
                  modelChild: systemsModel,
                }}
              />
              <ElectroSelectExtra
                field={form.$('location.locationSystem')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  data: actionsListData[5],
                  disabled: !form.$('location.locationRoom').extra.selectID,
                }}
                props={{
                  model: systemsModel,
                }}
              />
            </FormSection>

            <FormSection form={form} section="additionalInformation">
              <ElectroTextField
                field={form.$('additionalInformation.comments')}
                props={{
                  multiline: true,
                  rows: 7,
                }}
              />
            </FormSection>
            <FormControls
              form={this.form}
              buttonText={
                this.getMode() === 'edit' ? t('general:actions.save') : t('general:actions.create')
              }
            />
          </FormWrapper>
        </PageWrapper>
        <ActionDrawerWrapper
          onActionCompleted={() => {
            /*overriding with empty fn*/
          }}
        >
          {
            {
              newCostCenter: (
                <AddNewCostCenter
                  parentForm={form}
                  accountsModel={accountsModel}
                  model={projectCostCenter}
                  localState={this.localState}
                />
              ),
              newCompany: (
                <AddNewCompany
                  parentForm={form}
                  accountsModel={accountsModel}
                  model={companyModel}
                  childmodel={sitesModel}
                  resetModels={[sitesModel, buildingsModel, roomsModel, systemsModel]}
                  resetFields={[
                    form.$('location.locationSite'),
                    form.$('location.locationBuilding'),
                    form.$('location.locationRoom'),
                    form.$('location.locationSystem'),
                  ]}
                />
              ),
              newSite: (
                <AddNewSite
                  parentForm={form}
                  accountsModel={accountsModel}
                  companyModel={companyModel}
                  model={sitesModel}
                  childmodel={buildingsModel}
                  resetModels={[buildingsModel, roomsModel, systemsModel]}
                  resetFields={[
                    form.$('location.locationBuilding'),
                    form.$('location.locationRoom'),
                    form.$('location.locationSystem'),
                  ]}
                />
              ),
              newBuilding: (
                <AddNewBuilding
                  parentForm={form}
                  accountsModel={accountsModel}
                  companyModel={companyModel}
                  model={buildingsModel}
                  childmodel={roomsModel}
                  resetModels={[roomsModel, systemsModel]}
                  resetFields={[form.$('location.locationRoom'), form.$('location.locationSystem')]}
                />
              ),
              newRoom: (
                <AddNewRoom
                  parentForm={form}
                  accountsModel={accountsModel}
                  companyModel={companyModel}
                  model={roomsModel}
                  childmodel={systemsModel}
                  resetModels={[systemsModel]}
                  resetFields={[form.$('location.locationSystem')]}
                />
              ),
              newSystem: (
                <AddNewSystem
                  parentForm={form}
                  accountsModel={accountsModel}
                  companyModel={companyModel}
                  model={systemsModel}
                />
              ),
            }[this.props.actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </React.Fragment>
    );
  }
}
